/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import useLogin from "../../hooks/useLogin";
import Title from "../../components/Form/Title";
import Moneda from "../../components/Presupuesto/Moneda";
import { PresupuestoResumen } from "../../service/presupuestoExcel";
import "./resumen.css";

const PESTANAS = [
  { clave: "portada", titulo: "Portada" },
  { clave: "esquema", titulo: "Esquema financiero" },
  { clave: "flujo", titulo: "Flujo de efectivo" },
];

/**
 * Las tres hojas del Excel que no son el desglose, calculadas desde las
 * partidas. El desglose se edita en la pantalla de presupuestos; aquí se
 * consulta lo que sale de él.
 */
export default function ResumenPresupuesto() {
  const { id } = useParams();
  const { token } = useLogin();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pestana, setPestana] = useState("portada");

  useEffect(() => {
    let cancelado = false;

    PresupuestoResumen(id, token)
      .then((respuesta) => {
        if (cancelado) return;

        if (!respuesta?.success) {
          throw new Error(respuesta?.message || "No se pudo cargar el presupuesto");
        }
        setDatos(respuesta.data);
      })
      .catch((error) => {
        if (!cancelado) {
          Swal.fire({ icon: "error", title: "No se pudo cargar", text: error.message });
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [id, token]);

  if (cargando) {
    return (
      <div className="container">
        <div className="crud-card resumen-vacio">Cargando el presupuesto...</div>
      </div>
    );
  }

  if (!datos) {
    return (
      <div className="container">
        <div className="crud-card resumen-vacio">
          No hay presupuesto que mostrar. Importa el Excel desde la lista de
          proyectos.
        </div>
      </div>
    );
  }

  const { proyecto, aportantes } = datos;

  return (
    <div className="container">
      <div className="crud-header">
        <Title title={proyecto.nombre || "Presupuesto"} className="crud-title" />
        <Link className="btn btn-outline-dark btn-sm" to={`/presupuestos/${id}`}>
          <i className="bi bi-list-columns me-1" aria-hidden="true"></i>
          Ver desglose
        </Link>
      </div>

      <div className="resumen-tabs" role="tablist">
        {PESTANAS.map((p) => (
          <button
            key={p.clave}
            type="button"
            role="tab"
            aria-selected={pestana === p.clave}
            className={`resumen-tab ${pestana === p.clave ? "is-activa" : ""}`}
            onClick={() => setPestana(p.clave)}
          >
            {p.titulo}
          </button>
        ))}
      </div>

      {pestana === "portada" && (
        <Portada proyecto={proyecto} aportantes={aportantes} portada={datos.portada} />
      )}
      {pestana === "esquema" && <Esquema informes={datos.esquema} />}
      {pestana === "flujo" && <Flujo flujo={datos.flujo} />}
    </div>
  );
}

// ---------------------------------------------------------------- portada

function Portada({ proyecto, aportantes, portada }) {
  // Cuentas abiertas: se ve lo que cuelga de cada una sin salir de la portada.
  const [abiertas, setAbiertas] = useState(new Set());

  const alternar = (cuenta) =>
    setAbiertas((previas) => {
      const siguientes = new Set(previas);
      if (siguientes.has(cuenta)) siguientes.delete(cuenta);
      else siguientes.add(cuenta);
      return siguientes;
    });

  const ficha = [
    ["Director", proyecto.director],
    ["Productor responsable", proyecto.productor],
    ["Guión", proyecto.guion],
    ["Director de fotografía", proyecto.director_fotografia],
    ["Pre-producción", proyecto.preproduccion],
    ["Rodaje", proyecto.rodaje],
    ["Post-producción", proyecto.postproduccion],
    ["Formato", proyecto.formato],
    ["Locaciones", proyecto.locaciones],
  ].filter(([, valor]) => valor);

  return (
    <>
      {ficha.length > 0 && (
        <div className="crud-card resumen-ficha">
          {ficha.map(([etiqueta, valor]) => (
            <div className="resumen-dato" key={etiqueta}>
              <span className="resumen-dato-etiqueta">{etiqueta}</span>
              <span className="resumen-dato-valor">{valor}</span>
            </div>
          ))}
        </div>
      )}

      <div className="crud-card">
        <div className="dtable-scroll">
          <table className="dtable resumen-tabla">
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Descripción</th>
                <th className="text-end">Total</th>
                {aportantes.map((a) => (
                  <th key={a.id} className="text-end">
                    {a.nombre}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {portada.etapas.map((etapa) => (
                <React.Fragment key={etapa.grupo}>
                  {etapa.renglones.map((r) => (
                    <React.Fragment key={r.cuenta}>
                      <tr>
                        <td>
                          <span className="resumen-celda-arbol">
                            {(r.partidas || []).length > 0 ? (
                              <button
                                type="button"
                                className="resumen-expandir"
                                onClick={() => alternar(r.cuenta)}
                                aria-expanded={abiertas.has(r.cuenta)}
                                aria-label={
                                  abiertas.has(r.cuenta)
                                    ? "Ocultar las partidas"
                                    : "Ver las partidas"
                                }
                              >
                                <i
                                  className={`bi ${
                                    abiertas.has(r.cuenta)
                                      ? "bi-dash-square"
                                      : "bi-plus-square"
                                  }`}
                                ></i>
                              </button>
                            ) : (
                              <span className="resumen-expandir-hueco" />
                            )}
                            {r.cuenta}
                          </span>
                        </td>
                        <td>{r.nombre}</td>
                        <td className="text-end">
                          <Moneda valor={r.total} />
                        </td>
                        {aportantes.map((a) => (
                          <td key={a.id} className="text-end">
                            <Moneda valor={r.aportaciones[a.id]} />
                          </td>
                        ))}
                      </tr>

                      {abiertas.has(r.cuenta) &&
                        (r.partidas || []).map((p) => (
                          <tr key={p.id} className="resumen-subfila">
                            <td>
                              <span
                                className="resumen-celda-arbol"
                                style={{ paddingLeft: `${1.1 + p.nivel * 1.1}rem` }}
                              >
                                {p.cuenta}
                              </span>
                            </td>
                            <td className={p.es_grupo ? "resumen-grupo" : undefined}>
                              {p.nombre}
                            </td>
                            <td className="text-end">
                              <Moneda valor={p.total} />
                            </td>
                            {aportantes.map((a) => (
                              <td key={a.id} className="text-end">
                                <Moneda valor={p.aportaciones[a.id]} />
                              </td>
                            ))}
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                  <tr className="resumen-subtotal">
                    <td></td>
                    <td>{etapa.titulo}</td>
                    <td className="text-end">
                      <Moneda valor={etapa.subtotal} />
                    </td>
                    {aportantes.map((a) => (
                      <td key={a.id}></td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}

              <tr className="resumen-total">
                <td></td>
                <td>GRAN TOTAL</td>
                <td className="text-end">
                  <Moneda valor={portada.gran_total} />
                </td>
                {aportantes.map((a) => (
                  <td key={a.id} className="text-end">
                    <Moneda valor={portada.por_aportante[a.id]} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- esquema

function Esquema({ informes }) {
  if (!informes.length) {
    return <div className="crud-card resumen-vacio">Este proyecto no tiene informes.</div>;
  }

  return (
    <div className="resumen-informes">
      {informes.map((informe) => (
        <div className="crud-card" key={informe.id}>
          <h3 className="resumen-informe-titulo">{informe.nombre}</h3>
          <div className="dtable-scroll">
            <table className="dtable resumen-tabla">
              <thead>
                <tr>
                  <th>Aportantes</th>
                  <th className="text-end">Efectivo con IVA</th>
                  <th className="text-end">Especie sin IVA</th>
                  <th className="text-end">Total</th>
                  <th className="text-end">Ejercido</th>
                  <th className="text-end">Por ejercer</th>
                </tr>
              </thead>
              <tbody>
                {informe.renglones.map((r) => (
                  <tr key={r.aportante_id}>
                    <td>{r.aportante}</td>
                    <td className="text-end"><Moneda valor={r.efectivo} /></td>
                    <td className="text-end"><Moneda valor={r.especie} /></td>
                    <td className="text-end"><Moneda valor={r.total} /></td>
                    <td className="text-end"><Moneda valor={r.ejercido} /></td>
                    <td className="text-end"><Moneda valor={r.por_ejercer} /></td>
                  </tr>
                ))}
                <tr className="resumen-total">
                  <td>Total Presupuesto</td>
                  <td className="text-end"><Moneda valor={informe.totales.efectivo} /></td>
                  <td className="text-end"><Moneda valor={informe.totales.especie} /></td>
                  <td className="text-end"><Moneda valor={informe.totales.total} /></td>
                  <td className="text-end"><Moneda valor={informe.totales.ejercido} /></td>
                  <td className="text-end"><Moneda valor={informe.totales.por_ejercer} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ flujo

function Flujo({ flujo }) {
  // El estado va antes de cualquier return: los hooks no pueden quedar dentro
  // de una rama condicional.
  const [soloConMonto, setSoloConMonto] = useState(true);

  // Sin periodos no hay nada que repartir en el tiempo.
  if (!flujo.periodos.length) {
    return (
      <div className="crud-card resumen-vacio">
        Este presupuesto todavía no tiene periodos de flujo.
      </div>
    );
  }

  const renglones = soloConMonto
    ? flujo.renglones.filter((r) => r.suma !== 0)
    : flujo.renglones;

  return (
    <div className="crud-card">
      <div className="resumen-barra">
        <label className="resumen-filtro">
          <input
            type="checkbox"
            checked={soloConMonto}
            onChange={(e) => setSoloConMonto(e.target.checked)}
          />
          Mostrar sólo las cuentas con movimiento
        </label>
        <span className="resumen-conteo">{renglones.length} renglones</span>
      </div>

      <div className="dtable-scroll">
        <table className="dtable resumen-tabla">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>Rubro</th>
              <th className="text-end">Total</th>
              {flujo.periodos.map((p) => (
                <th key={p.id} className="text-center" colSpan={2}>
                  {p.etapa}
                  <div className="resumen-periodo">{p.nombre}</div>
                </th>
              ))}
              <th className="text-end">Suma</th>
            </tr>
            <tr className="resumen-subencabezado">
              <th></th>
              <th></th>
              <th></th>
              {flujo.periodos.map((p) => (
                <React.Fragment key={p.id}>
                  <th className="text-end">Efectivo</th>
                  <th className="text-end">Especie</th>
                </React.Fragment>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((r, i) => (
              <tr key={`${r.cuenta || "s"}-${i}`} className={r.es_cuenta ? "resumen-cuenta" : ""}>
                <td>{r.cuenta}</td>
                <td style={{ paddingLeft: `${0.5 + r.nivel * 1.1}rem` }}>{r.nombre}</td>
                <td className="text-end"><Moneda valor={r.total} /></td>
                {flujo.periodos.map((p) => (
                  <React.Fragment key={p.id}>
                    <td className="text-end">
                      <Moneda valor={r.periodos[p.id]?.efectivo} />
                    </td>
                    <td className="text-end">
                      <Moneda valor={r.periodos[p.id]?.especie} />
                    </td>
                  </React.Fragment>
                ))}
                <td className="text-end"><Moneda valor={r.suma} /></td>
              </tr>
            ))}

            <tr className="resumen-total">
              <td></td>
              <td>Total</td>
              <td></td>
              {flujo.periodos.map((p) => (
                <React.Fragment key={p.id}>
                  <td className="text-end">
                    <Moneda valor={flujo.totales[p.id]?.efectivo} />
                  </td>
                  <td className="text-end">
                    <Moneda valor={flujo.totales[p.id]?.especie} />
                  </td>
                </React.Fragment>
              ))}
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
